interface GenerationReportFooterArgs {
  siteName: string;
  sheetName: string;
  dateKeys: string[];
}

const extractIsoDate = (value: string): string => {
  if (!value) return '';
  // Handles composite keys like '2026-07-12_42791' -> '2026-07-12'
  if (typeof value === 'string' && value.includes('_')) {
    const parts = value.split('_');
    if (/^\d{4}-\d{2}-\d{2}$/.test(parts[0])) return parts[0];
  }
  const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
  if (match) return match[1];
  return String(value).trim();
};

const parseIsoDate = (value: string): Date | null => {
  if (!value) return null;
  const cleanDate = extractIsoDate(value);
  if (!cleanDate) return null;

  if (/^\d{4}-\d{2}-\d{2}$/.test(cleanDate)) {
    const [y, m, d] = cleanDate.split('-').map(Number);
    const parsed = new Date(y, m - 1, d);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const parsed = new Date(`${cleanDate}T00:00:00`);
  if (!Number.isNaN(parsed.getTime())) return parsed;

  const fallback = new Date(cleanDate);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
};

const formatDayMonth = (value: string): string => {
  const date = parseIsoDate(value);
  if (!date) return value;
  const day = String(date.getDate()).padStart(2, '0');
  const month = date.toLocaleString('en-GB', { month: 'short' });
  return `${day}${month}`;
};

export const buildGenerationReportFooterText = ({
  siteName,
  sheetName,
  dateKeys
}: GenerationReportFooterArgs): string => {
  const validSortedDates = Array.from(
    new Set((dateKeys || []).map(extractIsoDate).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b));

  const daysCount = validSortedDates.length;
  const dayLabel = daysCount === 1 ? 'Day' : 'Days';
  const safeSiteName = (siteName || 'Site').trim();

  if (daysCount === 0) {
    return `${safeSiteName} ${sheetName} | 0 Days`;
  }

  const start = formatDayMonth(validSortedDates[0]);
  const end = formatDayMonth(validSortedDates[daysCount - 1]);

  return `${safeSiteName} ${sheetName} | ${daysCount} ${dayLabel} (${start} - ${end})`;
};
